// "Where?" dropdown surfaced under the desktop pill's Locație segment
// (and the FilterModal city field) when the input is focused. The only
// row is the geolocate shortcut ("Aproape de tine") — the visitor types
// their city straight into the input above, so we no longer surface a
// predefined city list (it advertised cities that may have no salons).
//
// Parent owns the open/close state and renders the click-outside
// overlay; this component is pure presentation + the geolocation
// handler. onPickCity fires with the snapped city name on success.
//
// Combobox keyboard support: the parent drives activeIndex via
// useComboboxNav (hooks/useComboboxNav.js). The single option (idx 0)
// is the geolocate row; the imperative `activate(idx)` method lets the
// parent trigger it on Enter without DOM focus on the button.

import { forwardRef, useImperativeHandle } from 'react';
import { FiCompass } from 'react-icons/fi';
import { geolocateNearestCity } from '../../utils/snapNearestCity';

// Only one option now (the geolocate row). Exported so consumers hand it
// to useComboboxNav as the optionCount without poking internals.
export const LOCATION_POPOVER_OPTION_COUNT = 1;

const LocationPopover = forwardRef(function LocationPopover({
  id,
  activeIndex = -1,
  onActiveIndexChange,
  onPickCity,
  className = '',
}, ref) {
  const handleGeolocate = async () => {
    const { city } = await geolocateNearestCity();
    if (city) onPickCity?.(city);
    // Silent on failure — the visitor falls back to typing their city
    // into the input above.
  };

  // Imperative API: parent calls ref.current.activate(idx) on Enter when
  // the option is highlighted via aria-activedescendant. Only idx 0 (the
  // geolocate row) exists now.
  useImperativeHandle(ref, () => ({
    activate: (idx) => {
      if (idx === 0) handleGeolocate();
    },
  }));

  const optionId = (idx) => (id ? `${id}-option-${idx}` : undefined);

  // Active rows take an explicit bg color rather than just the hover
  // pseudo-class, so keyboard nav (which never triggers :hover) still
  // gets a visible highlight. hover: still applies when the mouse
  // takes over, but the keyboard-driven state wins via specificity.
  const rowClass = (active) =>
    `flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
      active ? 'bg-primary-50' : 'hover:bg-ink/5'
    }`;

  return (
    // `id` is forwarded so each consumer can wire it as the target of
    // its input's aria-controls (the WAI-ARIA combobox pattern requires
    // a programmatic link from the textbox to the popup listbox).
    <div
      id={id}
      role="listbox"
      aria-label="Destinații sugerate"
      className={`overflow-hidden rounded-2xl border border-ink/10 bg-cream-50 shadow-2xl ${className}`}
    >
      <ul className="max-h-[420px] overflow-y-auto py-2">
        {/* Nearby — geolocation (index 0 in the keyboard nav) */}
        <li>
          <button
            type="button"
            id={optionId(0)}
            role="option"
            aria-selected={activeIndex === 0}
            onMouseEnter={() => onActiveIndexChange?.(0)}
            onClick={handleGeolocate}
            className={rowClass(activeIndex === 0)}
          >
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-pastel-sky">
              <FiCompass className="h-5 w-5 text-pastel-sky-deep" aria-hidden="true" />
            </span>
            <span className="truncate text-sm font-semibold text-ink">
              Aproape de tine
            </span>
          </button>
        </li>
      </ul>
    </div>
  );
});

export default LocationPopover;
